import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ArrowLeft, Clock, User, Calendar, Tag, Share2 } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, articleSchema, breadcrumbSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMAGES = [
    "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?q=80&w=800&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1540747737956-378724044402?q=80&w=800&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1518063319789-7217e6706b04?q=80&w=800&auto=format&fit=crop",
];

const formatContent = (content) => {
    if (!content) return "";
    
    // Check if the content contains HTML tags like <p>, <br>, etc.
    const hasHtml = /<[a-z][\s\S]*>/i.test(content);
    if (hasHtml) {
        return content;
    }
    
    // Convert plain text with newlines into HTML paragraphs and line breaks
    return content
        .split(/\n\s*\n/)
        .map(paragraph => `<p>${paragraph.replace(/\n/g, "<br />")}</p>`)
        .join("");
};

const ArticleDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);

        axios
            .get(`${API}/articles/${slug}`)
            .then(({ data }) => {
                if (active) {
                    setArticle(data);
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch article details:", err);
                if (active) {
                    setError("Article introuvable");
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [slug]);

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: article?.title,
                text: article?.excerpt,
                url: window.location.href,
            }).catch(console.error);
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert("Lien copié dans le presse-papiers !");
        }
    };

    if (loading) {
        return (
            <div className="bg-[#07111D] min-h-screen text-white flex flex-col items-center justify-center gap-3 py-20">
                <Clock className="animate-spin text-[#00C853]" size={30} />
                <span className="text-gray-400 text-sm">Chargement de l'article...</span>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="bg-[#07111D] min-h-screen text-white flex flex-col items-center justify-center gap-4 py-20">
                <h2 className="text-2xl font-bold text-red-500">Oups !</h2>
                <p className="text-gray-400 text-sm">{error || "Cet article n'existe pas ou a été supprimé."}</p>
                <Link to="/" className="btn-outline flex items-center gap-2 !py-2 !px-4 text-xs font-semibold">
                    <ArrowLeft size={14} /> Retour à l'accueil
                </Link>
            </div>
        );
    }

    const defaultImage = FALLBACK_IMAGES[Math.abs(slug.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)) % FALLBACK_IMAGES.length];
    const imageToUse = article.image || defaultImage;

    return (
        <div data-testid={`article-detail-${slug}`} className="bg-[#07111D] min-h-screen text-white pb-20">
            <SEO
                title={`${article.title} | Pronospot.com`}
                description={article.excerpt}
                image={imageToUse}
                jsonLd={[
                    articleSchema({
                        title: article.title,
                        description: article.excerpt,
                        author: article.author || "Pronospot",
                        datePublished: article.date,
                        url: `https://pronospot.com/articles/${article.slug}`,
                    }),
                    breadcrumbSchema([
                        { name: "Accueil", url: "https://pronospot.com/" },
                        { name: "Actualités", url: "https://pronospot.com/" },
                        {
                            name: article.title,
                            url: `https://pronospot.com/articles/${article.slug}`,
                        },
                    ]),
                ]}
            />

            {/* HEADER HERO */}
            <div className="relative w-full h-[320px] md:h-[420px] overflow-hidden bg-black/40">
                <img
                    src={imageToUse}
                    alt={article.title}
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07111D] via-[#07111D]/40 to-transparent" />
                
                {/* Content over hero */}
                <div className="absolute bottom-0 left-0 right-0 py-8">
                    <div className="container-x">
                        <Breadcrumbs
                            items={[
                                { label: "Actualités", to: "/" },
                                { label: article.title },
                            ]}
                        />

                        <div className="flex flex-wrap items-center gap-3 mt-4 mb-3">
                            <span className="px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-black bg-[#00C853] flex items-center gap-1">
                                <Tag size={10} />
                                {article.category}
                            </span>
                            <span className="text-xs text-gray-300 font-medium flex items-center gap-1">
                                <Clock size={12} className="text-gray-400" />
                                {article.read_time} de lecture
                            </span>
                        </div>

                        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight max-w-4xl">
                            {article.title}
                        </h1>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="container-x mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12">
                    
                    {/* LEFT COLUMN: Main body */}
                    <article className="min-w-0">
                        {/* Meta strip */}
                        <div className="flex items-center justify-between border-b border-[#2A4A6B]/50 pb-5 mb-8 text-gray-400 text-xs sm:text-sm">
                            <div className="flex items-center gap-5">
                                <span className="flex items-center gap-1.5 font-semibold text-white">
                                    <User size={14} className="text-[#00C853]" />
                                    {article.author}
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Calendar size={14} className="text-gray-500" />
                                    {article.date}
                                </span>
                            </div>

                            <button
                                onClick={handleShare}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#2A4A6B] hover:border-[#00C853] hover:text-[#00C853] transition-all bg-transparent font-medium cursor-pointer"
                                title="Partager l'article"
                            >
                                <Share2 size={13} />
                                <span className="hidden sm:inline">Partager</span>
                            </button>
                        </div>

                        {/* Article Excerpt */}
                        {article.excerpt && (
                            <p className="text-base sm:text-lg text-gray-300 leading-relaxed italic border-l-2 border-l-[#00C853] pl-4 mb-8 font-medium">
                                {article.excerpt}
                            </p>
                        )}

                        {/* Article Content Render */}
                        <div 
                            className="prose-custom max-w-none text-gray-300 leading-relaxed space-y-6 text-sm sm:text-base"
                            dangerouslySetInnerHTML={{ __html: formatContent(article.content) }}
                        />

                        {/* Author Box */}
                        <div className="card-elev p-6 flex items-center gap-4 mt-16 border border-[#2A4A6B]/30 hover:border-[#00C853]/20 transition-all duration-300">
                            <div className="w-14 h-14 rounded-full bg-[#162236] border border-[#2A4A6B]/50 flex items-center justify-center flex-shrink-0">
                                <User size={24} className="text-[#00C853]" />
                            </div>
                            <div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Rédigé par</div>
                                <div className="font-heading font-black text-white text-base mt-0.5">{article.author}</div>
                                <div className="text-xs text-gray-400 mt-1">Équipe Pronospot — Expert en paris sportifs et analyses de football</div>
                            </div>
                        </div>
                    </article>

                    {/* RIGHT COLUMN: Sidebar widgets */}
                    <aside className="space-y-6">
                        {/* Action Box */}
                        <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B] text-center">
                            <h3 className="font-heading font-black text-white text-sm uppercase tracking-wider mb-3">
                                Nos Pronostics Foot
                            </h3>
                            <p className="text-xs text-gray-400 mb-4 leading-relaxed">
                                Découvrez les meilleures cotes du jour et nos analyses détaillées sur les matchs de la semaine.
                            </p>
                            <Link to="/cotes" className="w-full btn-outline inline-flex items-center justify-center gap-1.5 !py-2.5 text-xs font-bold uppercase tracking-wide">
                                Comparer les cotes
                                <ChevronRight size={14} />
                            </Link>
                        </div>

                        {/* Back home widget */}
                        <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B] flex flex-col gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="w-full bg-[#162236] hover:bg-[#162236]/80 border border-[#2A4A6B]/50 hover:border-[#00C853]/30 text-white rounded-lg py-2.5 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer"
                            >
                                <ArrowLeft size={13} />
                                Page précédente
                            </button>
                            <Link
                                to="/"
                                className="w-full bg-[#00C853] hover:bg-[#00C853]/90 text-black rounded-lg py-2.5 text-xs font-black uppercase tracking-wider text-center flex items-center justify-center transition-all"
                            >
                                Retour à l'accueil
                            </Link>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default ArticleDetail;
